import Plot from "react-plotly.js";
import React, {useEffect, useRef, useState} from 'react';
import {VEHICLE_DIMENSIONS_KEY_ENUM} from "./constants";
import {getMaxFinite, getMinFinite} from "../../../../helpers/graphs";

const Plotly2DTracesFront = (props) => {
  const length = 50;

  const [radarX, setRadarX] = useState([]);
  const [radarY, setRadarY] = useState([]);

  const [camerax, setCameraX] = useState([]);
  const [cameray, setCameraY] = useState([]);

  const [imux, setIMUX] = useState([]);
  const [imuy, setIMUY] = useState([]);

  const [antennax, setAntennaX] = useState([]);
  const [antennay, setAntennaY] = useState([]);

  const [lidarX, setLidarX] = useState([]);
  const [lidarY, setLidarY] = useState([]);

  const [ultrasonicsX, setUltrasonicsX] = useState([]);
  const [ultrasonicsY, setUltrasonicsY] = useState([]);

  Math.radians = (degrees) => degrees * Math.PI / 180;
  const [vehicle_height, setVehicleHeight] = useState(-1);
  const [vehicle_length, setVehicleLength] = useState(-1);
  const [vehicle_width, setVehicleWidth] = useState(-1);
  const [left, setLeft] = useState(-1);
  const [offset, setOffset] = useState(-1);
  useEffect(() => {
    let dimensions_list = props?.dimensionsList;
    dimensions_list?.forEach((obj) => {
      if (obj.property_name.trim() === VEHICLE_DIMENSIONS_KEY_ENUM.VEHICLE_HEIGHT) {
        setVehicleHeight(obj.value);
      }
      if (obj.property_name.trim() === VEHICLE_DIMENSIONS_KEY_ENUM.VEHICLE_LENGTH) {
        setVehicleLength(obj.value);
      }
      if (obj.property_name.trim() === VEHICLE_DIMENSIONS_KEY_ENUM.VEHICLE_WIDTH) {
        setVehicleWidth(obj.value);
      }
      if (obj.property_name.trim() === VEHICLE_DIMENSIONS_KEY_ENUM.NX_FRONT_OF_VEHICLE_X) {
        setLeft(obj.value);
      }
      if (obj.property_name.trim() === VEHICLE_DIMENSIONS_KEY_ENUM.NX_FRONT_OF_VEHICLE_Z) {
        setOffset(obj.value);
      }
    });
  }, [props]);

  const [x0, setX0] = useState(-1);
  const [y0, setY0] = useState(-1);
  const [x1, setX1] = useState(-1);
  const [y1, setY1] = useState(-1);

  useEffect(() => {
    setX0((vehicle_width / 2));
    setY0(vehicle_height + offset);
    setX1(-vehicle_width / 2);
    setY1(0 + offset);
  }, [left, vehicle_width, vehicle_length, vehicle_height]);

  const [globalMin, setGlobalMin] = useState(null);
  const [globalMax, setGlobalMax] = useState(null);

  const getPlotRange = (absoluteMin, absoluteMax) => {
    if (globalMin == null || globalMin > absoluteMin) {
      setGlobalMin(absoluteMin);
    }
    if (globalMax == null || globalMax < absoluteMax) {
      setGlobalMax(absoluteMax);
    }
  };

  const processSensorData = (sensorData) => {
    const x = [];
    const y = [];
    const a = [];
    const z = [];
    const yaw = [];
    let absoluteMax = null;

    for (let sensorLocation in sensorData) {
      const data = sensorData[sensorLocation];
      const _x = data?.origin_point_x;
      const _y = data?.origin_point_y;
      const _z = data?.origin_point_z;
      const _a = data?.pitch;
      if (_x != null && _y != null && _z != null) {
        x.push(_x);
        y.push(_y);
        z.push(_z);
        a.push(_a);
        yaw.push(data?.yaw);
      }
    }

    if (x.length > 0 && y.length > 0) {
      absoluteMax = getMaxFinite([...x, ...y, ...z]);
    }

    return {x, y, z, a, yaw, plotRange: absoluteMax ? [-absoluteMax + 1000, absoluteMax + 1000] : null};
  };

  const initSensorData = (sensorData, setX, setY, setA) => {
    if (sensorData) {
      const {x, y, z, a, yaw, plotRange} = processSensorData(sensorData);
      if (setX != null)
        setX(y);
      if (setY != null)
        setY(z);
      if (setA != null)
        setA(a);
    } else {
      if (setX != null)
        setX([]);
      if (setY != null)
        setY([]);
      if (setA != null)
        setA([]);
    }
  };

  useEffect(() => {
    const sensorsDataMap = props?.plotlyTraces;
    initSensorData(sensorsDataMap?.['Radar'], setRadarX, setRadarY, null);
    initSensorData(sensorsDataMap?.['Camera'], setCameraX, setCameraY, null);
    initSensorData(sensorsDataMap?.['IMU'], setIMUX, setIMUY, null);
    initSensorData(sensorsDataMap?.['Antenna'], setAntennaX, setAntennaY, null);
    initSensorData(sensorsDataMap?.['Lidar'], setLidarX, setLidarY, null);
    initSensorData(sensorsDataMap?.['Ultrasonic'], setUltrasonicsX, setUltrasonicsY, null);
  }, [props]);

  useEffect(() => {
    const allX = [...radarX, ...camerax, ...imux, ...antennax, ...lidarX, ...ultrasonicsX];
    const allY = [...radarY, ...cameray, ...imuy, ...antennay, ...lidarY, ...ultrasonicsY];

    if (allX.length > 0 && allY.length > 0) {
      const absoluteMax = getMaxFinite([...allX, ...allY]);
      const absoluteMin = getMinFinite([...allX, ...allY]);
      const abs = Math.max(Math.abs(absoluteMax), Math.abs(absoluteMin));
      getPlotRange(-abs - 1000, abs + 1000);
    }
  }, [radarX, camerax, imux, antennax, lidarX, ultrasonicsX, radarY, cameray, imuy, antennay, lidarY, ultrasonicsY]);

  const [radar, setRadar] = useState([]);
  useEffect(() => {
    const _radar = {
      x: radarX,
      y: radarY,
      mode: 'markers',
      type: 'scatter',
      marker: {size: 8, symbol: 'square'},
      line: {shape: 'linear', color: 'rgb(245,177,29)'},
      name: 'Radar',
      showlegend: 'true'
    };
    setRadar(_radar);
  }, [radarX, radarY]);

  const [camera, setCamera] = useState([]);
  useEffect(() => {
    const _camera = {
      x: camerax,
      y: cameray,
      mode: 'markers',
      type: 'scatter',
      marker: {size: 8},
      line: {shape: 'linear', color: 'rgb(111,8,138)'},
      name: 'Camera',
      showlegend: 'true'
    };
    setCamera(_camera);
  }, [camerax, cameray]);

  const [imu, setIMU] = useState([]);
  useEffect(() => {
    const _imu = {
      x: imux,
      y: imuy,
      mode: 'markers',
      type: 'scatter',
      marker: {size: 8},
      line: {shape: 'linear', color: 'green'},
      name: 'IMU',
      showlegend: 'true'
    };
    setIMU(_imu);
  }, [imux, imuy]);

  const [antenna, setAntenna] = useState([]);
  useEffect(() => {
    const _antenna = {
      x: antennax,
      y: antennay,
      mode: 'markers',
      type: 'scatter',
      marker: {size: 8},
      line: {shape: 'linear', color: 'blue'},
      name: 'Antenna',
      showlegend: 'true'
    };
    setAntenna(_antenna);
  }, [antennax, antennay]);

  const [lidar, setLidar] = useState([]);
  useEffect(() => {
    const _lidar = {
      x: lidarX,
      y: lidarY,
      mode: 'markers',
      type: 'scatter',
      marker: {size: 8},
      line: {shape: 'linear', color: 'red'},
      name: 'Lidar',
      showlegend: 'true'
    };
    setLidar(_lidar);
  }, [lidarX, lidarY]);

  const [ultrasonics, setUltrasonics] = useState([]);
  useEffect(() => {
    const _ultrasonics = {
      x: ultrasonicsX,
      y: ultrasonicsY,
      mode: 'markers',
      type: 'scatter',
      marker: {size: 8},
      line: {shape: 'linear', color: 'brown'},
      name: 'Ultrasonics',
      showlegend: 'true'
    };
    setUltrasonics(_ultrasonics);
  }, [ultrasonicsX, ultrasonicsY]);

  const [data, setData] = useState([]);
  useEffect(() => {
    setData([radar, camera, imu, antenna, lidar, ultrasonics]);
  }, [radar, camera, imu, antenna, lidar, ultrasonics]);

  const useForceUpdate = () => {
    const [value, setValue] = useState(0);
    return () => setValue(value => value + 1);
  };
  const forceUpdate = useForceUpdate();

  const handleClick = () => {
    forceUpdate();
  };

  const config = {
    modeBarButtonsToAdd: [
      {
        name: 'Square Plot',
        icon: {
          width: 80,
          height: 80,
          path: 'M78,22V79H21V22H78m9-9H12V88H87V13ZM68,46.22H31V54H68ZM53,32H45.22V69H53Z',
          transform: 'matrix(1 0 0 1 -10 -10)'
        },
        click: () => handleClick(),
      },
    ]
  };
  const confref = useRef(null);
  confref.current = config;

  return <Plot
    config={confref.current}
    useResizeHandler={true}
    style={{height: 800, width: 800, paddingBottom: "3px"}}
    data={data}
    layout={{
      title: 'Front View',
      shapes: [
        {
          type: 'rect',
          xref: 'x',
          yref: 'y',
          x0: x0,
          y0: y0,
          x1: x1,
          y1: y1,
          line: {
            color: 'black',
            width: 3
          },
          fillcolor: 'rgba(128, 128, 128, 0.3)'
        },
        {
          type: 'line',
          x0: -globalMax,
          y0: offset,
          x1: globalMax,
          y1: offset,
          line: {
            color: 'grey',
            width: 2
          },
          name: 'Ground plane',
          showlegend: true
        }
      ],
      legend: {
        // tracegroupgap: 100,

        orientation: 'h',
        y: 1.05,

        // y: 1,
        // x: 0,
        // xanchor: 'left'
      },
      xaxis: {
        title: 'Y Axis (mm)',
        rangemode: "tozero",
        nticks: 8,
        range: [globalMin, globalMax]
      },
      yaxis: {
        title: 'Z Axis (mm)',
        rangemode: "tozero",
        nticks: 8,
        range: [globalMin, globalMax]
      }
    }}
  />;
};

export default Plotly2DTracesFront;